import { Button, EmptyState, Icons } from '@dbrain/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMe } from '../../api/hooks.js';
import { isSuperAdmin, useProductOn } from '../../api/license.js';
import { switchTarget, useMeta, type Experience } from '../../experience.js';
import { LicenseUploadDialog } from './LicenseUploadDialog.js';

/**
 * A product the license does not have (D-125): its address shows only this page. The other product is a click
 * away when it is on; a super admin can upload a license here too (the API lets this address do that).
 */
export function ProductOffPage({ experience, onLogout, loggingOut }: { experience: Experience; onLogout: () => void; loggingOut: boolean }) {
  const { t } = useTranslation();
  const me = useMe().data;
  const meta = useMeta();
  const other: Experience = experience === 'chat' ? 'code' : 'chat';
  const otherOn = useProductOn(other);
  const [upload, setUpload] = useState(false);
  const admin = isSuperAdmin(me);
  return (
    <div className="plain-shell">
      <header className="plain-shell__top">
        <span className="plain-shell__brand">{t('common.appName')}</span>
      </header>
      <main id="main" className="plain-shell__main product-off" data-testid="product-off">
        <EmptyState
          icon={<Icons.lock />}
          title={t('license.productOff.title')}
          action={
            <div className="product-off__actions">
              {otherOn ? (
                <Button onClick={() => window.location.assign(switchTarget(meta.data, experience, '/'))} data-testid="product-off-switch">
                  {t(experience === 'chat' ? 'experience.toCode' : 'experience.toChat')}
                </Button>
              ) : null}
              {admin ? (
                <Button variant={otherOn ? 'secondary' : 'primary'} icon={<Icons.upload size={16} />} onClick={() => setUpload(true)} data-testid="product-off-upload">
                  {t('license.upload.title')}
                </Button>
              ) : null}
              <Button variant="ghost" busy={loggingOut} onClick={onLogout} data-testid="product-off-logout">
                {t('nav.logout')}
              </Button>
            </div>
          }
        >
          {t(admin ? 'license.productOff.bodyAdmin' : 'license.productOff.body', { product: t(`experience.${experience}`) })}
        </EmptyState>
      </main>
      {upload ? <LicenseUploadDialog onClose={() => setUpload(false)} /> : null}
    </div>
  );
}
